import React from "react";
import { Form, Input, Select, InputNumber, Switch, Button, Space, Card, Tag, Tooltip, Row, Col } from "antd";
import { PlusOutlined, DeleteOutlined, HolderOutlined, CopyOutlined } from "@ant-design/icons";
import type { FormFieldDefinition, SelectOption } from "../types/aiTools";
import { FIELD_TYPES } from "../types/aiTools";

interface FormFieldsEditorProps {
  value?: FormFieldDefinition[];
  onChange?: (value: FormFieldDefinition[]) => void;
}

const generateFieldId = () => `field_${Date.now().toString(36)}`;

const createEmptyField = (order: number): FormFieldDefinition => ({
  id: generateFieldId(),
  label: "",
  fieldType: "text",
  placeholder: "",
  description: "",
  required: false,
  editable: true,
  order,
  selectOptions: [],
});

export const FormFieldsEditor: React.FC<FormFieldsEditorProps> = ({ value = [], onChange }) => {
  const addField = () => {
    const newField = createEmptyField(value.length + 1);
    onChange?.([...value, newField]);
  };

  const removeField = (index: number) => {
    const newFields = [...value];
    newFields.splice(index, 1);
    newFields.forEach((f, i) => (f.order = i + 1));
    onChange?.(newFields);
  };

  const duplicateField = (index: number) => {
    const fieldToCopy = value[index];
    const newField: FormFieldDefinition = {
      ...fieldToCopy,
      id: generateFieldId(),
      label: `${fieldToCopy.label} (kopia)`,
      order: value.length + 1,
    };
    onChange?.([...value, newField]);
  };

  const updateField = (index: number, updates: Partial<FormFieldDefinition>) => {
    const newFields = [...value];
    newFields[index] = { ...newFields[index], ...updates };
    onChange?.(newFields);
  };

  const moveField = (fromIndex: number, direction: "up" | "down") => {
    const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= value.length) return;

    const newFields = [...value];
    [newFields[fromIndex], newFields[toIndex]] = [newFields[toIndex], newFields[fromIndex]];
    newFields.forEach((f, i) => (f.order = i + 1));
    onChange?.(newFields);
  };

  return (
    <div>
      <Space orientation="vertical" style={{ width: "100%" }} size="small">
        {value
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((field, index) => (
            <Card
              key={field.id}
              size="small"
              title={
                <Space>
                  <HolderOutlined style={{ cursor: "move", color: "#999" }} />
                  <Tag color={field.required ? "red" : "default"}>
                    {field.required ? "Wymagane" : "Opcjonalne"}
                  </Tag>
                  <Tag>{FIELD_TYPES.find((t) => t.value === field.fieldType)?.label || field.fieldType}</Tag>
                  <strong>{field.label || field.id}</strong>
                </Space>
              }
              extra={
                <Space size="small">
                  <Tooltip title="Duplikuj">
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => duplicateField(index)}
                    />
                  </Tooltip>
                  <Tooltip title="Przesuń w górę">
                    <Button
                      type="text"
                      size="small"
                      icon="↑"
                      disabled={index === 0}
                      onClick={() => moveField(index, "up")}
                    />
                  </Tooltip>
                  <Tooltip title="Przesuń w dół">
                    <Button
                      type="text"
                      size="small"
                      icon="↓"
                      disabled={index === value.length - 1}
                      onClick={() => moveField(index, "down")}
                    />
                  </Tooltip>
                  <Tooltip title="Usuń">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeField(index)}
                    />
                  </Tooltip>
                </Space>
              }
            >
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="ID" style={{ marginBottom: 8 }}>
                    <Input
                      value={field.id}
                      onChange={(e) => updateField(index, { id: e.target.value })}
                      placeholder="field_xxx"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Label" style={{ marginBottom: 8 }}>
                    <Input
                      value={field.label}
                      onChange={(e) => updateField(index, { label: e.target.value })}
                      placeholder="Nazwa pola"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Typ pola" style={{ marginBottom: 8 }}>
                    <Select
                      value={field.fieldType}
                      onChange={(v) => updateField(index, { fieldType: v })}
                      options={[...FIELD_TYPES]}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Kolejność" style={{ marginBottom: 8 }}>
                    <InputNumber
                      value={field.order}
                      onChange={(v) => updateField(index, { order: v || 0 })}
                      min={1}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Placeholder" style={{ marginBottom: 8 }}>
                    <Input
                      value={field.placeholder}
                      onChange={(e) => updateField(index, { placeholder: e.target.value })}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Opis" style={{ marginBottom: 8 }}>
                    <Input
                      value={field.description}
                      onChange={(e) => updateField(index, { description: e.target.value })}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Wymagane" style={{ marginBottom: 8 }}>
                    <Switch
                      checked={field.required}
                      onChange={(v) => updateField(index, { required: v })}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Edytowalne" style={{ marginBottom: 8 }}>
                    <Switch
                      checked={field.editable}
                      onChange={(v) => updateField(index, { editable: v })}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {["text", "textarea"].includes(field.fieldType) && (
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item label="Min. długość" style={{ marginBottom: 8 }}>
                      <InputNumber
                        value={field.minLength}
                        onChange={(v) => updateField(index, { minLength: v || undefined })}
                        min={0}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Max. długość" style={{ marginBottom: 8 }}>
                      <InputNumber
                        value={field.maxLength}
                        onChange={(v) => updateField(index, { maxLength: v || undefined })}
                        min={0}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {field.fieldType === "textarea" && (
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item label="Min. linii" style={{ marginBottom: 8 }}>
                      <InputNumber
                        value={field.minLines}
                        onChange={(v) => updateField(index, { minLines: v || undefined })}
                        min={1}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Max. linii" style={{ marginBottom: 8 }}>
                      <InputNumber
                        value={field.maxLines}
                        onChange={(v) => updateField(index, { maxLines: v || undefined })}
                        min={1}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {field.fieldType === "number" && (
                <Row gutter={16}>
                  <Col span={4}>
                    <Form.Item label="Min" style={{ marginBottom: 8 }}>
                      <InputNumber
                        value={field.min}
                        onChange={(v) => updateField(index, { min: v || undefined })}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item label="Max" style={{ marginBottom: 8 }}>
                      <InputNumber
                        value={field.max}
                        onChange={(v) => updateField(index, { max: v || undefined })}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item label="Krok" style={{ marginBottom: 8 }}>
                      <InputNumber
                        value={field.step}
                        onChange={(v) => updateField(index, { step: v || undefined })}
                        min={0.01}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {["select", "multiselect", "radio", "segmented"].includes(field.fieldType) && (
                <SelectOptionsEditor
                  value={field.selectOptions}
                  onChange={(options) => updateField(index, { selectOptions: options })}
                />
              )}

              {field.fieldType === "file" && (
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="Dozwolone rozszerzenia" style={{ marginBottom: 8 }}>
                      <Select
                        mode="tags"
                        value={field.allowedExtensions}
                        onChange={(v) => updateField(index, { allowedExtensions: v })}
                        placeholder="pdf, doc, png..."
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item label="Wiele plików" style={{ marginBottom: 8 }}>
                      <Switch
                        checked={field.allowMultiple}
                        onChange={(v) => updateField(index, { allowMultiple: v })}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="Max. rozmiar (MB)" style={{ marginBottom: 8 }}>
                      <InputNumber
                        value={field.maxFileSize ? field.maxFileSize / 1024 / 1024 : undefined}
                        onChange={(v) =>
                          updateField(index, { maxFileSize: v ? v * 1024 * 1024 : undefined })
                        }
                        min={0}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="Domyślna wartość" style={{ marginBottom: 0 }}>
                    <Input
                      value={
                        field.defaultValue !== undefined
                          ? typeof field.defaultValue === "string"
                            ? field.defaultValue
                            : JSON.stringify(field.defaultValue)
                          : ""
                      }
                      onChange={(e) => {
                        let val: unknown = e.target.value;
                        try {
                          val = JSON.parse(e.target.value);
                        } catch {
                          // Keep as string
                        }
                        updateField(index, { defaultValue: val });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Zależy od (ID)" style={{ marginBottom: 0 }}>
                    <Select
                      value={field.dependsOn}
                      onChange={(v) => updateField(index, { dependsOn: v || undefined })}
                      allowClear
                      placeholder="Wybierz pole..."
                      options={value
                        .filter((f) => f.id !== field.id)
                        .map((f) => ({ value: f.id, label: f.label || f.id }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Wartość zależności" style={{ marginBottom: 0 }}>
                    <Input
                      value={
                        field.dependsOnValue !== undefined
                          ? typeof field.dependsOnValue === "string"
                            ? field.dependsOnValue
                            : JSON.stringify(field.dependsOnValue)
                          : ""
                      }
                      onChange={(e) => {
                        let val: unknown = e.target.value;
                        try {
                          val = JSON.parse(e.target.value);
                        } catch {
                          // Keep as string
                        }
                        updateField(index, { dependsOnValue: val });
                      }}
                      disabled={!field.dependsOn}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Widoczne w wyniku" style={{ marginBottom: 0 }}>
                    <Switch
                      checked={field.visibleInResult ?? true}
                      onChange={(v) => updateField(index, { visibleInResult: v })}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          ))}

        <Button type="dashed" onClick={addField} icon={<PlusOutlined />} block>
          Dodaj pole formularza
        </Button>
      </Space>
    </div>
  );
};

interface SelectOptionsEditorProps {
  value?: SelectOption[];
  onChange?: (value: SelectOption[]) => void;
}

const SelectOptionsEditor: React.FC<SelectOptionsEditorProps> = ({ value = [], onChange }) => {
  const addOption = () => {
    onChange?.([...value, { value: "", label: "" }]);
  };

  const updateOption = (index: number, updates: Partial<SelectOption>) => {
    const newOptions = [...value];
    newOptions[index] = { ...newOptions[index], ...updates };
    onChange?.(newOptions);
  };

  const removeOption = (index: number) => {
    const newOptions = [...value];
    newOptions.splice(index, 1);
    onChange?.(newOptions);
  };

  return (
    <div style={{ marginTop: 8 }}>
      <strong>Opcje:</strong>
      <Space orientation="vertical" style={{ width: "100%", marginTop: 8 }}>
        {value.map((opt, idx) => (
          <Row key={idx} gutter={8} align="middle">
            <Col span={6}>
              <Input
                placeholder="Wartość (value)"
                value={opt.value}
                onChange={(e) => updateOption(idx, { value: e.target.value })}
              />
            </Col>
            <Col span={6}>
              <Input
                placeholder="Etykieta (label)"
                value={opt.label}
                onChange={(e) => updateOption(idx, { label: e.target.value })}
              />
            </Col>
            <Col span={6}>
              <Input
                placeholder="Ikona (opcjonalnie)"
                value={opt.icon}
                onChange={(e) => updateOption(idx, { icon: e.target.value || undefined })}
              />
            </Col>
            <Col span={5}>
              <Input
                placeholder="Opis (opcjonalnie)"
                value={opt.description}
                onChange={(e) => updateOption(idx, { description: e.target.value || undefined })}
              />
            </Col>
            <Col span={1}>
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeOption(idx)}
              />
            </Col>
          </Row>
        ))}
        <Button type="dashed" size="small" onClick={addOption} icon={<PlusOutlined />}>
          Dodaj opcję
        </Button>
      </Space>
    </div>
  );
};

export default FormFieldsEditor;
